import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REALMS = ["Virtual Running Event", "Race Travel (Marathon Trips)", "Coaching & Training Plans", "Trekking Expedition", "Curated Events", "Bespoke Journey"];

const inputCls =
  "w-full bg-[#FBF9F5]/60 border border-[#0D1117]/15 rounded-xl px-5 py-4 text-sm text-[#0D1117] placeholder:text-[#6B7280] focus:border-[#FF4E00]/60 focus:bg-[#FBF9F5] focus:outline-none transition-all duration-300";

export default function Enquiry() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", realm: REALMS[0], message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    const apply = (trip) =>
      setForm((f) => ({
        ...f,
        realm: "Race Travel (Marathon Trips)",
        message: `I'm interested in the ${trip} trip. Please share the package details.`,
      }));
    const stored = sessionStorage.getItem("fg_trip");
    if (stored) {
      apply(stored);
      sessionStorage.removeItem("fg_trip");
    }
    const coaching = sessionStorage.getItem("fg_enquiry");
    if (coaching) {
      try {
        const detail = JSON.parse(coaching);
        setForm((f) => ({ ...f, realm: detail.realm || f.realm, message: detail.message || f.message }));
      } catch {}
      sessionStorage.removeItem("fg_enquiry");
    }
    const handler = (e) => apply(e.detail);
    window.addEventListener("fg:enquire", handler);
    return () => window.removeEventListener("fg:enquire", handler);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Received. The Fitygrity team will reach out within 48 hours.");
      setForm({ name: "", email: "", phone: "", realm: REALMS[0], message: "" });
    } catch {
      toast.error("Something interrupted the transmission. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="enquire" className="px-6 sm:px-12 lg:px-20 py-24 sm:py-32 lg:py-40 border-t border-[#0D1117]/10">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24 items-start">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4E00] mb-5">Enquiries</p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-[#0D1117] tracking-tight leading-tight">
            The finish line doesn't define you.
            <span className="italic text-[#FF4E00]"> The journey does.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-[#374151] leading-relaxed max-w-md">
            Whether it's your first 10K, a marathon, an ultra, or a personal fitness challenge,
            tell us where you want to go. We'll help shape the experience around your ambition.
          </p>
          <div className="mt-10 space-y-4">
            <div className="flex items-center gap-4">
              <span className="w-2 h-2 rounded-full bg-[#FF4E00]" />
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#374151]">
                Response within 48 hours
              </span>
            </div>
            <div className="flex items-center gap-4">
              <span className="w-2 h-2 rounded-full bg-[#FF4E00]" />
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#374151]">
                Private. No-spam correspondence.
              </span>
            </div>
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: "easeOut" }}
          onSubmit={submit}
          data-testid="enquiry-form"
          className="rounded-3xl border border-[#0D1117]/10 bg-[#FFFFFF]/80 backdrop-blur p-7 sm:p-10 space-y-6"
        >
          <div>
            <label htmlFor="enquiry-name" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#374151] mb-2.5">
              Full Name
            </label>
            <input
              id="enquiry-name"
              data-testid="enquiry-input-name"
              required
              value={form.name}
              onChange={update("name")}
              placeholder="Aarav Sherpa"
              className={inputCls}
            />
          </div>
          <div>
            <label htmlFor="enquiry-email" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#374151] mb-2.5">
              Email
            </label>
            <input
              id="enquiry-email"
              data-testid="enquiry-input-email"
              type="email"
              required
              value={form.email}
              onChange={update("email")}
              placeholder="you@summit.com"
              className={inputCls}
            />
          </div>
          <div>
            <label htmlFor="enquiry-phone" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#374151] mb-2.5">
              Mobile <span className="text-[#6B7280] normal-case tracking-normal">(optional — for a quicker call back)</span>
            </label>
            <input
              id="enquiry-phone"
              data-testid="enquiry-input-phone"
              type="tel"
              value={form.phone}
              onChange={update("phone")}
              placeholder="+91 98100 12345"
              className={inputCls}
            />
          </div>
          <div>
            <label htmlFor="enquiry-realm" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#374151] mb-2.5">
              Preferred Realm
            </label>
            <select
              id="enquiry-realm"
              data-testid="enquiry-select-realm"
              value={form.realm}
              onChange={update("realm")}
              className={`${inputCls} appearance-none cursor-pointer`}
            >
              {REALMS.map((r) => (
                <option key={r} value={r} className="bg-[#FFFFFF]">
                  {r}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="enquiry-message" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#374151] mb-2.5">
              Message
            </label>
            <textarea
              id="enquiry-message"
              data-testid="enquiry-textarea-message"
              rows={4}
              value={form.message}
              onChange={update("message")}
              placeholder="Tell us about the summit you have in mind..."
              className={`${inputCls} resize-none`}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="enquiry-submit-button"
            className="group w-full inline-flex items-center justify-center gap-3 bg-[#FF4E00] hover:bg-[#E04000] disabled:opacity-60 text-[#FBF9F5] font-mono text-xs uppercase tracking-[0.25em] px-8 py-5 rounded-full transition-all duration-300"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Transmitting
              </>
            ) : (
              <>
                Send Enquiry
                <Send className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
              </>
            )}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
